import type { LunarOverviewResponseDto } from '@lunary/api-client'
import { HBars } from '../../shared/ui/charts/hbars'
import type { Series } from '../../shared/ui/charts/legend'
import { daysBetween, formatNumber } from '../../shared/ui/charts/scale'
import { StackedBars } from '../../shared/ui/charts/stacked-bars'
import { KIND_LABELS } from './labels'
import styles from './lunars.module.css'

const SERIES: readonly Series[] = [
  { key: 'bought', label: 'Куплено', color: 'var(--series-4)' },
  { key: 'free', label: 'Бесплатно', color: 'var(--series-1)' },
  { key: 'spent', label: 'Потрачено', color: 'var(--series-5)' },
]

const EARN_KINDS = ['day_reward', 'week_reward', 'task_reward', 'merge_carry']
const SPEND_KINDS = ['reading_purchase', 'reading_upgrade', 'extra_pick', 'gift_purchase', 'day_repair']

export function OverviewTab({ data }: { data: LunarOverviewResponseDto }) {
  const days = daysBetween(data.period.from, data.period.to)
  const byDay = new Map(data.by_day.map((r) => [r.day, r]))
  const value = (day: string, key: string): number => {
    const row = byDay.get(day)
    if (!row) return 0
    if (key === 'bought') return row.bought
    if (key === 'free') return row.free
    return Math.max(0, row.spent)
  }
  const kinds = new Map(data.by_kind.map((r) => [r.kind, r]))
  const items = (list: readonly string[], sign: 1 | -1) =>
    list.map((k) => ({ key: k, label: KIND_LABELS[k] ?? k, value: Math.max(0, sign * (kinds.get(k)?.amount ?? 0)) }))
  const total = Math.max(1, data.circulation_paid + data.circulation_free)
  const paidShare = Math.min(100, Math.max(0, Math.round((data.circulation_paid / total) * 100)))

  return (
    <div className={styles.stack}>
      <div className={styles.split}>
        <div className="card-panel">
          <p className={styles.caption}>В обращении: сумма балансов равна сумме журнала</p>
          <div className={styles.big}>{formatNumber(data.circulation_total)}</div>
          <div className={styles.bar} role="img" aria-label={`Купленных ${paidShare}%`}>
            <div style={{ width: `${paidShare}%`, background: 'var(--series-4)' }} />
            <div style={{ width: `${100 - paidShare}%`, background: 'var(--series-1)' }} />
          </div>
          <div className={styles.line}>
            <span>Купленные</span>
            <b>{formatNumber(data.circulation_paid)}</b>
          </div>
          <div className={styles.line}>
            <span>Бесплатные</span>
            <b>{formatNumber(data.circulation_free)}</b>
          </div>
          <div className={styles.line}>
            <span>Обязательство по купленным</span>
            <b>≈ {formatNumber(data.paid_liability_rub)} ₽</b>
          </div>
          <p className={styles.caption} style={{ marginTop: 8 }}>
            Траты списываются сначала с бесплатных, поэтому разбивка расчётная.
          </p>
        </div>
        <div className="card-panel">
          <p className={styles.caption}>За период</p>
          <div className={styles.line}>
            <span>Куплено</span>
            <b>+{formatNumber(data.bought)}</b>
          </div>
          <div className={styles.line}>
            <span>Начислено бесплатно</span>
            <b>+{formatNumber(data.earned_free)}</b>
          </div>
          <div className={styles.line}>
            <span>Потрачено (за вычетом возвратов)</span>
            <b>−{formatNumber(data.spent)}</b>
          </div>
          <div className={styles.line}>
            <span>Списано при возвратах оплат</span>
            <b>−{formatNumber(data.refunded_lunars)}</b>
          </div>
        </div>
      </div>
      <div className="card-panel">
        <p className={styles.caption}>Куплено, начислено бесплатно и потрачено по дням</p>
        <StackedBars label="Лунары по дням" days={days} series={SERIES} value={value} height={240} />
      </div>
      <div className={styles.split}>
        <div className="card-panel">
          <p className={styles.caption}>Откуда бесплатные</p>
          <HBars label="Бесплатные начисления" color="var(--series-1)" items={items(EARN_KINDS, 1)} />
        </div>
        <div className="card-panel">
          <p className={styles.caption}>Куда уходят</p>
          <HBars label="Траты" color="var(--series-5)" items={items(SPEND_KINDS, -1)} />
        </div>
      </div>
    </div>
  )
}
